import { NgClass } from '@angular/common';
import { Component, effect, input, OnDestroy, signal, viewChild } from '@angular/core';
import { EPlatform } from '@enums/campaign.enum';
import { SkeletonLoaderComponent } from '../skeleton-loader/skeleton-loader.component';
import { ISkeletonColumn } from '../skeleton-loader/skeleton-loader.interface';
import { IVideoPlayer } from './embedded-video-player.interface';
import { TwitchPlayerComponent } from './platforms/twitch-player/twitch-player.component';
import { YoutubePlayerComponent } from './platforms/youtube-player/youtube-player.component';

@Component({
  imports: [TwitchPlayerComponent, SkeletonLoaderComponent, NgClass, YoutubePlayerComponent],
  selector: 'app-embedded-video-player',
  styleUrl: './embedded-video-player.component.scss',
  templateUrl: './embedded-video-player.component.html',
})
export class EmbeddedVideoPlayerComponent implements OnDestroy {
  public readonly videoUrl = input.required<string>();
  public readonly platform = input.required<EPlatform>();

  public readonly autoPlay = input<boolean>(false);
  public readonly seekToTime = input<number | null>(null);

  public readonly twitchPlayer = viewChild<TwitchPlayerComponent>(TwitchPlayerComponent);
  public readonly youtubePlayer = viewChild<YoutubePlayerComponent>(YoutubePlayerComponent);

  public readonly isPlaying = signal<boolean>(false);
  public readonly currentTime = signal<number>(0);
  public readonly duration = signal<number>(0);
  public readonly volume = signal<number>(1);
  public readonly isMuted = signal<boolean>(false);
  public readonly playerReady = signal<boolean>(false);

  public readonly EPlatform = EPlatform;
  public readonly skeletonVideoColumns: ISkeletonColumn[] = [
    {
      className: 'w-full',
      height: '630px',
      type: 'text',
    },
  ];

  public readonly skeletonVideoRows = 1;

  constructor() {
    effect(() => {
      const timeToSeek = this.seekToTime();

      if (timeToSeek !== null) {
        this.seek(timeToSeek);
      }
    });

    effect(() => {
      if (this.videoUrl()) {
        this.resetPlayer();
      }
    });
  }

  private get videoPlayer(): IVideoPlayer | undefined {
    if (this.platform() === EPlatform.twitch) {
      return this.twitchPlayer();
    }

    if (this.platform() === EPlatform.youtube) {
      return this.youtubePlayer();
    }

    return undefined;
  }

  public play(): void {
    if (this.videoPlayer) {
      this.videoPlayer.play();
      this.isPlaying.set(true);
    }
  }

  public pause(): void {
    if (this.videoPlayer) {
      this.videoPlayer.pause();
      this.isPlaying.set(false);
    }
  }

  public togglePlay(): void {
    if (this.isPlaying()) {
      this.pause();
    } else {
      this.play();
    }
  }

  public seek(time: number): void {
    if (this.videoPlayer) {
      this.videoPlayer.seek(time);
      this.currentTime.set(time);
    }
  }

  public setVolume(volume: number): void {
    if (!this.videoPlayer) {
      return;
    }

    this.videoPlayer.setVolume(volume);
    this.volume.set(volume);

    if (volume === 0) {
      this.isMuted.set(true);
    } else if (this.isMuted()) {
      this.isMuted.set(false);
    }
  }

  public toggleMute(): void {
    if (!this.videoPlayer) {
      return;
    }

    if (this.isMuted()) {
      this.videoPlayer.unmute();
      this.isMuted.set(false);
    } else {
      this.videoPlayer.mute();
      this.isMuted.set(true);
    }
  }

  public updatePlayerState(isPlaying: boolean, currentTime: number, duration: number): void {
    this.isPlaying.set(isPlaying);
    this.currentTime.set(currentTime);
    this.duration.set(duration);
  }

  public ngOnDestroy(): void {
    this.resetPlayer();
  }

  private resetPlayer(): void {
    if (!this.videoPlayer) {
      return;
    }

    this.videoPlayer.pause();
    this.isPlaying.set(false);
    this.currentTime.set(0);
    this.duration.set(0);
  }
}
